"use client"

import { BuildingStorefront, MapPin, Phone } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { type ReactNode, useRef, useState } from "react"

const liveHelp = {
  body: "For assistance, email us using the form or the contact details below. Call us directly during official hours for help or to address any concerns.",
  phoneLabel: `Call/Whatsapp: ${process.env.NEXT_PUBLIC_BRAND_SUPPORT_PHONE || ""}`,
  phoneHref: `tel:${process.env.NEXT_PUBLIC_BRAND_SUPPORT_PHONE || ""}`,
  email: process.env.NEXT_PUBLIC_BRAND_SUPPORT_EMAIL || "",
}

const corporateOffice = {
  title: "Corporate Office",
  lines: [
    "Khanpara, Duptara - 1460, Araihazar",
    "Narayanganj, Bangladesh.",
    "Mobile: +88 01844244188",
  ],
  phoneHref: "tel:+8801844244188",
}

const outlets = [
  {
    title: "Jamuna Future Park Outlet",
    lines: [
      "GD-018, East Court, Ground Floor,",
      "Jamuna Future Park, Baridhara",
      "Mobile: +88 01844244189",
    ],
  },
  {
    title: "Banani Outlet",
    lines: [
      "Alisons Tower (1st Floor), Road 11",
      "Block E, Banani, Dhaka 1213",
      "Mobile: +88 01844244192",
    ],
  },
  {
    title: "Bashundhara Outlet",
    lines: [
      "Bashundhara City Shopping Complex,",
      "Level 3, Block C, Shop 12-14,",
      "Panthapath, Dhaka 1215",
      "Mobile: +88 01844244190",
    ],
  },
  {
    title: "Mirpur Outlet",
    lines: [
      "Shop: E/1, Level: 2, Rupayan Latifa Shamsuddin Square,",
      "Mirpur 1, Dhaka 1216",
      "Mobile: +88 01844244191",
    ],
  },
]

export default function ContactPageTemplate() {
  const formRef = useRef<HTMLFormElement>(null)
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle")
  const [errorMsg, setErrorMsg] = useState("")

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus("sending")
    setErrorMsg("")
    const fd = new FormData(e.currentTarget)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fd.get("name"),
          email: fd.get("email"),
          phone: fd.get("phone"),
          comment: fd.get("comment"),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || "Submission failed")
      }
      setStatus("done")
      formRef.current?.reset()
    } catch (err: unknown) {
      setStatus("error")
      setErrorMsg(err instanceof Error ? err.message : "Submission failed. Please email us directly.")
    }
  }

  return (
    <main className="bg-[#FFFFFF] text-[#1C1C18]">
      <section className="relative overflow-hidden py-16 md:py-28">
        <div className="absolute inset-0 bg-[#F5F5F5]" />
        <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(90deg,#262626_1px,transparent_1px),linear-gradient(180deg,#262626_1px,transparent_1px)] [background-size:48px_48px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/30 via-[#FFFFFF]/70 to-[#FFFFFF]" />

        <div className="relative mx-auto flex min-h-[260px] max-w-[1440px] flex-col items-center justify-center px-5 text-center md:min-h-[360px] md:px-20">
          <p className="text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#5E5E5B]">
            Customer Care
          </p>
          <h1 className="mt-5 font-serif text-[40px] font-normal leading-[1.2] text-[#262626] md:text-[64px] md:leading-[1.1]">
            CONTACT US
          </h1>
          <p className="mt-6 max-w-2xl text-[18px] leading-[1.6] text-[#5E5E5B]">
            We create timeless fashion and interiors that blend elegance,
            comfort, and individuality - inspiring a lifestyle that feels truly
            yours.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 py-16 md:px-20 md:py-28 lg:grid-cols-12 lg:gap-24">
        <div className="lg:col-span-7">
          <div className="max-w-3xl">
            <p className="text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#5E5E5B]">
              Get In Touch
            </p>
            <h2 className="mt-4 font-serif text-[32px] font-normal leading-[1.3] text-[#262626] md:text-[48px] md:leading-[1.2]">
              Send Us An Email
            </h2>
            <p className="mt-3 text-[16px] leading-[1.6] text-[#5E5E5B]">
              Ask us anything! We&apos;re here to help.
            </p>
          </div>

          {status === "done" ? (
            <div className="mt-10 rounded border border-[#D4E8D4] bg-[#F5F5F5] px-8 py-10 text-center">
              <p className="font-serif text-[28px] text-[#262626]">Message Sent</p>
              <p className="mt-3 text-[16px] leading-[1.6] text-[#5E5E5B]">
                Thank you for reaching out. We&apos;ll get back to you shortly.
              </p>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className="mt-10 space-y-8">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <ContactField label="Name" name="name" placeholder="Name" />
                <ContactField
                  label="Phone"
                  name="phone"
                  placeholder="Phone"
                  type="tel"
                />
              </div>

              <ContactField
                label="Email Address*"
                name="email"
                placeholder="Email address"
                required
                type="email"
              />

              <div className="space-y-2">
                <label
                  htmlFor="comment"
                  className="block text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#5E5E5B]"
                >
                  Comment
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  placeholder="Comment"
                  rows={5}
                  className="w-full resize-none border-0 border-b border-[#262626]/25 bg-transparent px-0 py-4 text-[16px] leading-[1.6] text-[#1C1C18] outline-none transition-colors placeholder:text-[#747878] focus:border-[#262626] focus:ring-0"
                />
              </div>

              {status === "error" && (
                <p className="text-sm text-red-600">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full border border-[#262626] bg-[#262626] px-12 py-4 text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#FFFFFF] transition-colors hover:bg-transparent hover:text-[#262626] disabled:opacity-60 md:w-auto"
              >
                {status === "sending" ? "SENDING..." : "SUBMIT CONTACT"}
              </button>
            </form>
          )}
        </div>

        <aside className="space-y-12 lg:col-span-5">
          <section>
            <h2 className="border-b border-[#C4C7C7] pb-4 font-serif text-[32px] font-normal leading-[1.3] text-[#262626]">
              Get In Touch
            </h2>

            <div className="mt-8 space-y-8">
              <InfoBlock
                icon={<Phone className="h-5 w-5" />}
                title="Live Help"
                body={liveHelp.body}
              >
                <a
                  href={liveHelp.phoneHref}
                  className="block transition-colors hover:text-[#5E5E5B]"
                >
                  {liveHelp.phoneLabel}
                </a>
                <a
                  href={`mailto:${liveHelp.email}`}
                  className="block transition-colors hover:text-[#5E5E5B]"
                >
                  {liveHelp.email}
                </a>
              </InfoBlock>

              <InfoBlock
                icon={<MapPin className="h-5 w-5" />}
                title={corporateOffice.title}
              >
                <address className="not-italic">
                  {corporateOffice.lines.map((line, index) =>
                    index === corporateOffice.lines.length - 1 ? (
                      <a
                        key={line}
                        href={corporateOffice.phoneHref}
                        className="block transition-colors hover:text-[#5E5E5B]"
                      >
                        {line}
                      </a>
                    ) : (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ),
                  )}
                </address>
              </InfoBlock>
            </div>
          </section>

          <section>
            <h2 className="border-b border-[#C4C7C7] pb-4 font-serif text-[32px] font-normal leading-[1.3] text-[#262626]">
              Our Outlets
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {outlets.map((outlet) => (
                <div
                  key={outlet.title}
                  className="border-l border-[#E8E8E8] pl-5"
                >
                  <h3 className="text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#262626]">
                    {outlet.title}
                  </h3>
                  <address className="mt-3 space-y-1 text-sm not-italic leading-6 text-[#5E5E5B]">
                    {outlet.lines.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </address>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </section>

      <section className="bg-[#F6F3ED] px-5 py-16 md:px-20 md:py-28">
        <div className="mx-auto flex max-w-[900px] flex-col items-center text-center">
          <BuildingStorefront className="h-8 w-8 text-[#262626]" />
          <h2 className="mt-6 font-serif text-[32px] font-normal leading-[1.3] text-[#262626] md:text-[48px] md:leading-[1.2]">
            Find Store Location Near you
          </h2>
          <p className="mt-5 max-w-2xl text-[16px] leading-[1.6] text-[#5E5E5B]">
            Our presence includes 04 stores throughout Bangladesh and ongoing
            expansion. Click Find Store for our store locations.
          </p>
          <LocalizedClientLink
            href="/location"
            className="mt-8 border border-[#262626] px-12 py-4 text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#262626] transition-colors hover:bg-[#262626] hover:text-[#FFFFFF]"
          >
            FIND A STORE
          </LocalizedClientLink>
        </div>
      </section>
    </main>
  )
}

function ContactField({
  label,
  name,
  placeholder,
  type = "text",
  required = false,
}: {
  label: string
  name: string
  placeholder: string
  type?: string
  required?: boolean
}) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={name}
        className="block text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#5E5E5B]"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        placeholder={placeholder}
        required={required}
        type={type}
        className="w-full border-0 border-b border-[#262626]/25 bg-transparent px-0 py-4 text-[16px] leading-[1.6] text-[#1C1C18] outline-none transition-colors placeholder:text-[#747878] focus:border-[#262626] focus:ring-0"
      />
    </div>
  )
}

function InfoBlock({
  icon,
  title,
  body,
  children,
}: {
  icon: ReactNode
  title: string
  body?: string
  children: ReactNode
}) {
  return (
    <div className="grid grid-cols-[40px_1fr] gap-4">
      <span className="flex h-10 w-10 items-center justify-center border border-[#E8E8E8] text-[#262626]">
        {icon}
      </span>
      <div>
        <h3 className="text-[12px] font-semibold uppercase leading-[18px] tracking-[0.15em] text-[#262626]">
          {title}
        </h3>
        {body && (
          <p className="mt-3 text-[16px] leading-[1.6] text-[#5E5E5B]">
            {body}
          </p>
        )}
        <div className="mt-4 space-y-1 text-[16px] leading-[1.6] text-[#1C1C18]">
          {children}
        </div>
      </div>
    </div>
  )
}
